import { RootState } from "@/redux/store";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { QueryAnswerType } from "@/components/FpAppV4/utils/fpAppV4Interface";
import { BASE_API_URL } from "@/constants";
import fpFetch from "@/services/fpFetch";

export const useQueryInfo = (qid: string) => {
  // Get workspaceId and existing query data from Redux
  const workspaceIdFromRedux = useSelector(
    (state: RootState) => state.fpAppV4Data.workspaceId
  );

  // Get workspaceId from URL path as fallback
  const location = useLocation();
  const workspaceIdFromPath = extractWorkspaceIdFromPath(location.pathname);

  // Use Redux workspaceId if available, otherwise fall back to URL path
  const workspaceId = workspaceIdFromRedux || workspaceIdFromPath || "";

  // Try to get existing query data from Redux first
  const queryFromRedux = useSelector((state: RootState) => {
    const queryAnswer = state.fpAppV4Data.queryAnswers.find(
      //@ts-expect-error bad redux types
      (qa) => qa.qid === qid
    );
    return queryAnswer;
  });

  return useQuery({
    queryKey: ["queryInfo", qid, workspaceId],
    queryFn: async () => {
      // If we already have data in Redux, use that
      if (queryFromRedux) {
        return queryFromRedux;
      }

      // Otherwise fetch workspace data and find the specific query
      const response = await fpFetch(
        `${BASE_API_URL}/workspaces/find?_id=${workspaceId}`
      );
      const data = await response.json();

      // Find the specific query in message_pairs
      const queryData = data?.message_pairs?.find(
        (item: any) => item._id === qid
      );

      if (!queryData) {
        throw new Error("Query not found");
      }

      // Transform to match Redux format
      const tableData = queryData.tableData;
      delete queryData.tableData;

      return {
        toolResponse: queryData,
        qid: queryData._id,
        added: true,
        tableData,
        status: queryData.status,
        toolId: queryData.agent,
        query: queryData.query,
        company: queryData.company,
      };
    },
    initialData: queryFromRedux,
    enabled: !!qid && !!workspaceId && !queryFromRedux,
  });
};

// Helper function to extract workspaceId from various URL patterns
function extractWorkspaceIdFromPath(pathname: string): string | null {
  // Handle patterns like:
  // /reports/ec64a39b-8c5a-46b1-8029-1c314dfd0447
  // /chat/ec64a39b-8c5a-46b1-8029-1c314dfd0447
  // /matrix/ec64a39b-8c5a-46b1-8029-1c314dfd0447
  const patterns = [
    /^\/reports\/([a-f0-9-]{36})$/,
    /^\/chat\/([a-f0-9-]{36})$/,
    /^\/matrix\/([a-f0-9-]{36})$/,
  ];

  for (const pattern of patterns) {
    const match = pathname.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
}

/**
 * Hook to get information about a specific query by its ID.
 *
 * Fetches just one query_answer (one message_pair) from the backend
 * rather than the entire workspace. The old path resolved this by
 * fetching the workspace via /workspaces/find and picking out one entry
 * from message_pairs — fine for small chat workspaces, but autofill
 * workspaces accumulate hundreds of queries, so opening a Source modal
 * was downloading megabytes per click. /queryResults/getQueryAnswer
 * returns one document with the same shape as one element of
 * message_pairs (see backend for the shared projection).
 *
 * @param qid - The query ID to fetch information for
 * @returns A React Query result containing the query information
 */
export const useComposedQueryInfo = (workspaceId: string, qid: string) => {
  return useQuery({
    queryKey: ["queryInfo", qid, workspaceId],
    queryFn: async () => {
      if (!workspaceId || !qid) {
        throw new Error("Missing identifiers");
      }

      const response = await fpFetch(
        `${BASE_API_URL}/queryResults/getQueryAnswer?id=${encodeURIComponent(qid)}`
      );
      if (!response.ok) {
        throw new Error("Query not found");
      }
      const queryData = await response.json();

      const tableData = queryData.tableData;
      const queryDataCopy = { ...queryData };
      delete queryDataCopy.tableData;

      return {
        toolResponse: queryDataCopy,
        qid: queryData._id,
        added: true,
        tableData,
        status: queryData.status,
        toolId: queryData.agent,
        query: queryData.query,
        company: queryData.company,
        answer: queryData.answer as QueryAnswerType["toolResponse"]["answer"],
      };
    },
    enabled: !!qid && !!workspaceId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Keep polling until we get a completed answer
    refetchInterval: (query) =>
      !query.state.data || query.state.data.status !== "completed"
        ? 1000
        : false,
    // Retry transient failures like "Query not found" a few times quickly
    retry: (failureCount, error) => failureCount < 8,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 3000),
  });
};
